import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Check, Eye, EyeOff, PlayCircle, Sunrise, Users } from 'lucide-react'
import { Modal, Select } from '../admin/primitives.js'
import {
  VISIBILITY_HINTS, VISIBILITY_LABELS, modeOf, violatedConstraint,
  type VisibilityMode, type WidgetCapabilities, type WidgetDef, type WidgetOptionGroup,
  type WidgetPlacement,
} from './registry.js'

/**
 * CONFIGURER UNE TUILE.
 *
 * Une surface de premier niveau, pas un formulaire d'administration. La
 * hiérarchie porte le sens : la tuile qu'on règle, puis QUAND elle
 * s'affiche, puis CE QU'ELLE MONTRE à l'ouverture, puis À QUOI elle se
 * compare. Les tuiles qui n'ont rien à régler ne montrent que la première
 * section — leur inventer des options produirait des contrôles qui ne font
 * rien.
 *
 * POURQUOI DES GROUPES. La première version alignait les réglages en une
 * colonne unique. Avec un seul réglage cela passait ; avec cinq, plus rien
 * ne disait que « Ventilation » et « Référence » ne répondent pas à la même
 * question. Les familles viennent des capacités envoyées par le serveur, pas
 * d'un découpage inventé ici.
 *
 * La modale édite le BROUILLON de la page et rien d'autre. Elle n'a pas de
 * bouton « Enregistrer » : c'est la barre flottante globale qui confirme,
 * une seule fois, pour l'ordre comme pour les réglages. Deux systèmes de
 * sauvegarde qui se contredisent seraient pires qu'aucun.
 */

const MODE_ICONS: Record<VisibilityMode, typeof Eye> = {
  ALWAYS: Eye,
  BEFORE_START: Sunrise,
  FROM_START: PlayCircle,
  HIDDEN: EyeOff,
}

const MODES: VisibilityMode[] = ['ALWAYS', 'BEFORE_START', 'FROM_START', 'HIDDEN']

/** Ordre d'apparition des familles. La vue par défaut ouvre « OPENING » :
 *  c'est le même sujet — ce que l'on voit en arrivant. */
const GROUP_ORDER: WidgetOptionGroup[] = ['OPENING', 'COMPARISON']
const GROUP_LABELS: Record<WidgetOptionGroup, string> = {
  OPENING: 'Affichage à l’ouverture',
  COMPARISON: 'Comparaison',
}
const GROUP_HINTS: Record<WidgetOptionGroup, string> = {
  OPENING: 'Ce que la tuile montre quand on arrive sur la soirée.',
  COMPARISON: 'Ce à quoi la soirée est comparée.',
}

export function WidgetConfigModal({ open, onClose, def, placement, capabilities, onChange }: {
  open: boolean
  onClose: () => void
  def: WidgetDef | null
  placement: WidgetPlacement | null
  capabilities: WidgetCapabilities | undefined
  onChange: (next: Partial<WidgetPlacement>) => void
}) {
  const mode = placement ? modeOf(placement) : 'ALWAYS'
  const config = useMemo(() => placement?.config ?? {}, [placement])

  const cap = capabilities
  const vueKey = cap?.defaultView?.key
  const contenuKey = cap?.content?.key
  const choix = useMemo(() => cap?.defaultView?.choices ?? [], [cap])

  /* Contenus activés — tout par défaut : une tuile neuve ne cache rien. */
  const actifs = useMemo<string[]>(() => {
    if (!contenuKey) return []
    const v = config[contenuKey]
    return Array.isArray(v) ? (v as string[]) : choix.map((c) => c.value)
  }, [config, contenuKey, choix])

  const vue = vueKey ? String(config[vueKey] ?? choix[0]?.value ?? '') : ''

  const [erreur, setErreur] = useState<string | null>(null)
  useEffect(() => { if (open) setErreur(null) }, [open, placement?.id])

  /* Combinaison impossible déjà en place : dite en permanence, pas
     seulement au moment du clic qui l'a créée. */
  const conflit = violatedConstraint(cap, config)

  /** Les familles réellement présentes, dans l'ordre. */
  const groupes = useMemo(() => {
    const opts = cap?.options ?? []
    return GROUP_ORDER
      .map((g) => ({ groupe: g, options: opts.filter((o) => (o.group ?? 'OPENING') === g) }))
      .filter((x) => x.options.length > 0)
  }, [cap])

  if (!def || !placement) return null

  const setConfig = (patch: Record<string, unknown>) => {
    const suivant = { ...config, ...patch }
    /*
     * On AVERTIT, on ne bloque pas. La combinaison refusée dépend de deux
     * réglages : interdire le second clic obligerait à deviner lequel des
     * deux changer. L'utilisateur voit le conflit, choisit lequel corriger,
     * et la barre d'enregistrement refusera tant qu'il subsiste.
     */
    const c = violatedConstraint(cap, suivant)
    setErreur(c ? c.message : null)
    onChange({ config: suivant })
  }

  const basculerContenu = (valeur: string) => {
    if (!contenuKey) return
    const dedans = actifs.includes(valeur)
    const suivant = dedans ? actifs.filter((x) => x !== valeur) : [...actifs, valeur]
    /*
     * Deux états absurdes refusés ICI, en plus du refus serveur : une tuile
     * sans aucune mesure n'aurait rien à montrer, et une vue par défaut
     * qu'on vient de retirer ouvrirait sur quelque chose d'inaccessible.
     * Le dire au moment du clic vaut mieux qu'un rejet à l'enregistrement.
     */
    if (suivant.length === 0) {
      setErreur('Au moins une mesure doit rester disponible.')
      return
    }
    if (vueKey && !suivant.includes(vue)) {
      setErreur(`« ${choix.find((c) => c.value === vue)?.label ?? vue} » est la mesure `
        + 'affichée à l’ouverture. Choisissez-en une autre avant de la retirer.')
      return
    }
    setErreur(null)
    onChange({ config: { ...config, [contenuKey]: suivant } })
  }

  const Icone = def.Icon ?? null
  const alerte = erreur ?? conflit?.message ?? null

  return (
    <Modal open={open} onClose={onClose} width={560} ariaLabel={`Configurer ${def.label}`}
      className="wc-modal" owning>
      <header className="wc-head">
        <span className="wc-head__icon" aria-hidden="true">
          {Icone ? <Icone size={20} /> : <Eye size={20} />}
        </span>
        <div className="wc-head__text">
          <p className="wc-head__eyebrow">Configurer la tuile</p>
          <h2 className="wc-head__title">{def.label}</h2>
          <p className="wc-head__desc">{def.description}</p>
        </div>
      </header>

      <div className="wc-body">
        <section className="wc-section">
          <h3 className="wc-section__title">Visibilité</h3>
          <p className="wc-section__hint">Quand cette tuile apparaît sur la page.</p>
          <div className="wc-modes" role="radiogroup" aria-label="Visibilité de la tuile">
            {MODES.map((m) => {
              const I = MODE_ICONS[m]
              const on = mode === m
              return (
                <button
                  key={m} type="button" role="radio" aria-checked={on}
                  className={`wc-mode ${on ? 'wc-mode--on' : ''}`}
                  onClick={() => onChange({ visibility: m })}
                >
                  <span className="wc-mode__icon"><I size={16} aria-hidden="true" /></span>
                  <span className="wc-mode__body">
                    <span className="wc-mode__label">{VISIBILITY_LABELS[m]}</span>
                    <span className="wc-mode__hint">{VISIBILITY_HINTS[m]}</span>
                  </span>
                  {on ? <Check size={16} className="wc-mode__check" aria-hidden="true" /> : null}
                </button>
              )
            })}
          </div>
        </section>

        {groupes.map(({ groupe, options }) => (
          <section className="wc-section" key={groupe}>
            <h3 className="wc-section__title">{GROUP_LABELS[groupe]}</h3>
            <p className="wc-section__hint">{GROUP_HINTS[groupe]}</p>
            <div className="wc-fields">
              {/* La vue par défaut est le premier champ de « l'ouverture » :
                  c'est la même question que les autres réglages du groupe. */}
              {groupe === 'OPENING' && cap?.defaultView ? (
                <label className="wc-field">
                  <span className="wc-field__label">{cap.defaultView.label}</span>
                  <Select
                    value={vue}
                    options={cap.defaultView.choices.filter((c) => actifs.length === 0 || actifs.includes(c.value))}
                    onChange={(v) => { setErreur(null); onChange({ config: { ...config, [cap.defaultView!.key]: v } }) }}
                    ariaLabel={cap.defaultView.label}
                  />
                  <span className="wc-field__hint">{cap.defaultView.hint}</span>
                </label>
              ) : null}
              {options.map((o) => (
                <label className="wc-field" key={o.key}>
                  <span className="wc-field__label">{o.label}</span>
                  <Select
                    value={String(config[o.key] ?? o.choices[0]?.value ?? '')}
                    options={o.choices}
                    onChange={(v) => setConfig({ [o.key]: v })}
                    ariaLabel={o.label}
                  />
                  <span className="wc-field__hint">{o.hint}</span>
                </label>
              ))}
            </div>
          </section>
        ))}

        {cap?.content ? (
          <section className="wc-section">
            <h3 className="wc-section__title">{cap.content.label}</h3>
            <p className="wc-section__hint">{cap.content.hint}</p>
            <div className="wc-checks">
              {choix.map((c) => {
                const on = actifs.includes(c.value)
                const ouverture = Boolean(vueKey) && c.value === vue
                return (
                  <button key={c.value} type="button" role="checkbox" aria-checked={on}
                    className={`wc-check ${on ? 'wc-check--on' : ''}`}
                    onClick={() => basculerContenu(c.value)}>
                    <span className="wc-check__box" aria-hidden="true">
                      {on ? <Check size={13} /> : null}
                    </span>
                    <span className="wc-check__label">{c.label}</span>
                    {/* Point, pas pastille : une seconde pastille DANS une
                        pastille faisait grandir celle-là seule, et la ligne
                        perdait son rythme. */}
                    {ouverture ? (
                      <span className="wc-check__mark" role="img"
                        aria-label="Affichée à l’ouverture" title="Affichée à l’ouverture">
                        <span className="wc-check__dot" aria-hidden="true" />
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
            <p className="wc-legend">
              <span className="wc-check__dot" aria-hidden="true" /> Mesure affichée à l’ouverture.
            </p>
          </section>
        ) : null}

        {!cap ? (
          <p className="wc-nothing">
            Cette tuile n’a pas d’autre réglage : elle montre toujours la même
            chose. Seule sa place et le moment où elle apparaît se règlent.
          </p>
        ) : null}
      </div>

      {alerte ? (
        <p className="wc-alert" role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>{alerte}</span>
        </p>
      ) : null}

      <footer className="wc-foot">
        {/* Une phrase, pas deux : ce pied doit se lire d'un coup d'œil, pas
            se déchiffrer comme une mention légale. */}
        <p className="wc-foot__note">
          <Users size={13} aria-hidden="true" />
          <span>Ce réglage vaut pour <strong>tout le monde</strong>, une fois enregistré en bas de page.</span>
        </p>
        <button type="button" className="lc-btn" onClick={onClose}>Terminé</button>
      </footer>
    </Modal>
  )
}
